import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { KeyRound, Loader2, ShieldCheck, LifeBuoy } from 'lucide-react';
import { platform, instances } from '../../services/superuserApi';

/** Mínimo de caracteres do motivo; o servidor valida o mesmo. */
export const MIN_REASON = 10;

/**
 * Grava a sessão de suporte onde o PORTAL do ginásio a procura (token + user) e devolve o caminho para onde ir.
 * Nunca toca na sessão da plataforma (superuser_token): terminar o suporte volta à consola sem novo login.
 */
export const startSupportSession = (grant) => {
  const user = { ...grant.user, supportExpiresAt: grant.expiresAt };
  localStorage.setItem('token', grant.token);
  localStorage.setItem('user', JSON.stringify(user));
  localStorage.removeItem('permissions');
  return '/admin';
};

const cell = { padding: '0.625rem 0.75rem', borderBottom: '1px solid var(--border-subtle)', fontSize: '0.8125rem', textAlign: 'left' };
const head = { ...cell, color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' };
const card = { background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', marginBottom: '1.5rem', overflow: 'hidden' };

export default function PlatformPanel({ navigate = (path) => { window.location.assign(path); } }) {
  const [operators, setOperators] = useState([]);
  const [gyms, setGyms] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [instanceId, setInstanceId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [ops, inst, log] = await Promise.all([
        platform.operators(),
        instances.getAll().catch(() => ({ data: [] })),
        platform.supportAudit(20).catch(() => ({ data: [] })),
      ]);
      setOperators(ops.data || []);
      setGyms((inst.data || []).filter((i) => i.isActive !== false));
      setAudit(log.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Não foi possível ler os operadores da plataforma.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reasonOk = reason.trim().length >= MIN_REASON;

  const open = async (event) => {
    event.preventDefault();
    if (!instanceId || !reasonOk) return;
    setBusy(true);
    try {
      const res = await platform.openSupportAccess(Number(instanceId), reason.trim());
      toast.success(`Acesso de suporte aberto em ${res.data.instance?.name}. Fica registado e termina em 30 minutos.`);
      if (res.data.url) {
        // Portal separado: o ginásio vive noutro endereço. O token vai no FRAGMENTO (nunca é enviado ao servidor nem fica nos logs) e a
        // sessão de suporte nasce lá; este navegador não guarda nada do ginásio.
        navigate(`${res.data.url}/suporte#t=${encodeURIComponent(res.data.token)}`);
      } else {
        navigate(startSupportSession(res.data));
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Não foi possível abrir o acesso de suporte.');
      setBusy(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem', color: 'var(--text-muted)' }}><Loader2 size={18} className="spin" /> A carregar…</div>;
  }

  const nameOf = (id) => gyms.find((g) => g.id === id)?.name || `#${id}`;

  return (
    <div data-testid="platform-panel">
      <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '0 0 0.25rem' }}>Plataforma</h2>
      <p style={{ color: 'var(--text-muted)', margin: '0 0 1.5rem', maxWidth: '60ch' }}>
        Os operadores são identidades globais: existem uma vez, no registo da plataforma, e são reconhecidos em todas as instâncias.
        Não são utilizadores de nenhum ginásio.
      </p>

      <section style={card} aria-labelledby="operators-title">
        <h3 id="operators-title" style={{ ...head, display: 'flex', gap: '0.5rem', alignItems: 'center', margin: 0 }}>
          <ShieldCheck size={14} aria-hidden="true" /> Operadores da plataforma
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={head}>Nome</th><th style={head}>Email</th><th style={head}>Estado</th></tr></thead>
          <tbody>
            {operators.map((o) => (
              <tr key={o.id}>
                <td style={cell}>{o.name}{o.isCurrent && <span style={{ color: 'var(--text-faint)' }}> (você)</span>}</td>
                <td style={cell}>{o.email}{o.isDefault && <span style={{ color: 'var(--accent-primary-hover)' }}> · predefinido</span>}</td>
                <td style={cell}>{o.isActive ? 'Ativo' : 'Suspenso'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section style={{ ...card, padding: '1.25rem' }} aria-labelledby="support-title">
        <h3 id="support-title" style={{ margin: '0 0 0.25rem', fontSize: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <LifeBuoy size={16} aria-hidden="true" /> Acesso de suporte a um ginásio
        </h3>
        <p style={{ color: 'var(--text-muted)', margin: '0 0 1rem', fontSize: '0.8125rem', maxWidth: '64ch' }}>
          A conta de plataforma não atua num ginásio por omissão. Para operar um, abra um acesso explícito: fica registado com o motivo,
          dura 30 minutos e não permite alterar utilizadores, perfis nem passwords.
        </p>
        <form onSubmit={open} style={{ display: 'grid', gap: '0.75rem', maxWidth: '36rem' }}>
          <label style={{ display: 'grid', gap: '0.25rem', fontSize: '0.8125rem' }}>
            Ginásio
            <select value={instanceId} onChange={(e) => setInstanceId(e.target.value)} required>
              <option value="">— escolher —</option>
              {gyms.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </label>
          <label style={{ display: 'grid', gap: '0.25rem', fontSize: '0.8125rem' }}>
            Motivo (obrigatório, fica no registo)
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={300} placeholder="Ex.: corrigir o horário a pedido do ginásio" />
          </label>
          <div>
            <button type="submit" className="btn btn-primary" disabled={busy || !instanceId || !reasonOk}>
              <KeyRound size={14} aria-hidden="true" /> {busy ? 'A abrir…' : 'Abrir acesso de suporte'}
            </button>
          </div>
        </form>
      </section>

      <section style={card} aria-labelledby="audit-title">
        <h3 id="audit-title" style={{ ...head, margin: 0 }}>Acessos de suporte recentes</h3>
        {audit.length === 0 ? (
          <p style={{ ...cell, color: 'var(--text-muted)', margin: 0 }}>Ainda não foi aberto nenhum acesso de suporte.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={head}>Quando</th><th style={head}>Quem</th><th style={head}>Ginásio</th><th style={head}>Motivo</th></tr></thead>
            <tbody>
              {audit.map((a) => (
                <tr key={a.id}>
                  <td style={cell}>{new Date(a.createdAt).toLocaleString('pt-PT')}</td>
                  <td style={cell}>{a.performedByName}</td>
                  <td style={cell}>{nameOf(a.gymInstanceId)}</td>
                  <td style={cell}>{a.details}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
